import { z } from 'zod';
import { CursorPageQuerySchema, IsoDateTimeSchema, UuidSchema, pageSchema } from './common.js';

/** Audit actions written in the Foundation phase. Later phases add theirs here. */
export const AUDIT_ACTIONS = [
  'auth.login',
  'auth.logout',
  'auth.refresh_reuse_detected',
  'auth.session_revoked',
  'org.created',
  'membership.created',
  'membership.reactivated',
  'membership.roles_changed',
  'membership.removed',
  // Pilot feedback #2: the admin named a member who had no name
  'membership.named',
  // ADR-042: the admin ticked a member's access areas
  'membership.access_changed',
  // Clinic Core — reads of clinical data are audited too (CLAUDE.md §9)
  'patient.created',
  'patient.updated',
  'patient.archived',
  'patient.restored',
  'medical_profile.viewed',
  'medical_profile.updated',
  'clinical_notes.viewed',
  'clinical_note.created',
  'service.created',
  'service.updated',
  'service.archived',
  'service.restored',
  'resource.created',
  'resource.updated',
  'resource.archived',
  'resource.restored',
  'appointment.created',
  'appointment.rescheduled',
  'appointment.status_changed',
  'visit.opened',
  'visit.item_added',
  'visit.item_updated',
  'visit.item_removed',
  'visit.completed',
  'visit.voided',
  // ADR-052: a line's renewal or touch-up date changed after completion; past services
  'visit.follow_up_changed',
  'prior_service.recorded',
  'prior_service.follow_up_changed',
  'prior_service.voided',
  'visit.clinical_viewed',
  'sms.marked',
  'sms.sent',
  'sms_template.updated',
  'treatment_method.created',
  'treatment_method.updated',
  'treatment_method.archived',
  // Clinic Inventory: every stock change is also a movement; these record who did it and why
  'inventory_item.created',
  'inventory_item.updated',
  'inventory_item.archived',
  'inventory_item.restored',
  'stock.received',
  'stock.cost_recorded',
  'stock.adjusted',
  'stock.written_off',
  'stock.reservation_released',
  'inventory_settings.updated',
  'visit.material_added',
  'visit.material_updated',
  'visit.material_removed',
  // Clinic Finance: the journal row is the record; these say who did it (charges and material
  // purchases are in the diff of visit.completed and stock.received)
  'journal.payment_recorded',
  'journal.discount_recorded',
  'journal.refund_recorded',
  'journal.adjustment_recorded',
  'journal.opening_balance_recorded',
  'journal.expense_recorded',
  'journal.entry_reversed',
  'visit.financially_reversed',
  'share_rule.created',
  'service_package.created',
  'service_package.updated',
  'service_package.archived',
  'service_package.restored',
  'patient_package.sold',
  'patient_package.cancelled',
  // Migration Tools
  'auth.reauthenticated',
  'patients.imported',
  'data_export.requested',
  'data_export.downloaded',
  'finance_export.downloaded',
  // Password login (ADR-033)
  'auth.password_link_issued',
  'auth.password_set',
  'auth.password_changed',
  // Pilot feedback: a doctor allows overlapping appointments for themselves (ADR-034)
  'practitioner_settings.updated',
  // Check-in is a clinic setting (ADR-035)
  'booking_settings.updated',
  // Who handles the laser desk and the laser till (ADR-036)
  'section_settings.updated',
  // Package terms and advances (ADR-037)
  'patient_package.extended',
  'finance_settings.updated',
  // What the clinic owes its suppliers (ADR-038)
  'vendor.created',
  'vendor.updated',
  'vendor.archived',
  'vendor.restored',
  'journal.vendor_payment_recorded',
  'journal.vendor_opening_recorded',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** Kinds of work the event log can be filtered by (pilot feedback #3). */
export const AUDIT_CATEGORIES = [
  'finance',
  'appointments',
  'visits',
  'patients',
  'inventory',
  'messages',
  'settings',
  'access',
  'data',
] as const;
export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];

/** By the part before the dot; the exceptions below win. */
const CATEGORY_BY_PREFIX: Readonly<Record<string, AuditCategory>> = {
  auth: 'access',
  org: 'access',
  membership: 'access',
  patient: 'patients',
  medical_profile: 'patients',
  clinical_notes: 'patients',
  clinical_note: 'patients',
  appointment: 'appointments',
  practitioner_settings: 'appointments',
  booking_settings: 'settings',
  section_settings: 'settings',
  finance_settings: 'settings',
  vendor: 'finance',
  visit: 'visits',
  prior_service: 'visits',
  inventory_item: 'inventory',
  stock: 'inventory',
  inventory_settings: 'inventory',
  journal: 'finance',
  share_rule: 'finance',
  service_package: 'finance',
  patient_package: 'finance',
  finance_export: 'finance',
  sms: 'messages',
  sms_template: 'messages',
  service: 'settings',
  resource: 'settings',
  treatment_method: 'settings',
  data_export: 'data',
};
const CATEGORY_BY_ACTION: Readonly<Partial<Record<AuditAction, AuditCategory>>> = {
  'visit.financially_reversed': 'finance',
  'patients.imported': 'data',
};

/** The category of an action, or null for an action this version does not know. */
export function auditCategoryOf(action: string): AuditCategory | null {
  return (
    CATEGORY_BY_ACTION[action as AuditAction] ?? CATEGORY_BY_PREFIX[action.slice(0, action.indexOf('.'))] ?? null
  );
}

export function auditActionsOf(category: AuditCategory): AuditAction[] {
  return AUDIT_ACTIONS.filter((a) => auditCategoryOf(a) === category);
}

export const AuditLogListQuerySchema = CursorPageQuerySchema.extend({
  category: z.enum(AUDIT_CATEGORIES).optional(),
});
export type AuditLogListQuery = z.infer<typeof AuditLogListQuerySchema>;

export const AuditLogSchema = z.object({
  id: UuidSchema,
  action: z.string(),
  entityType: z.string(),
  entityId: UuidSchema.nullable(),
  actorUserId: UuidSchema.nullable(),
  /** The member's name in this clinic; null for a system action or someone no longer listed. */
  actorName: z.string().nullable(),
  diff: z.unknown().nullable(),
  requestId: z.string().nullable(),
  ip: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
});
export type AuditLogView = z.infer<typeof AuditLogSchema>;

export const AuditLogListResponseSchema = pageSchema(AuditLogSchema);
export type AuditLogListResponse = z.infer<typeof AuditLogListResponseSchema>;
